import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { getFacilities, getFacilityFeatureFlags } from "../../lib/services/facilities";
import { generatePatAiTriage, getPatBoard, issuePatIntakeLink, revokePatIntakeLink, updatePatReviewAction } from "../../lib/services/patAutomation";

export function PatWorkspacePage() {
  const queryClient = useQueryClient();
  const [selectedFacility, setSelectedFacility] = useState("");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({});

  const featureFlagsQuery = useQuery({
    queryKey: ["pat-feature-flags"],
    queryFn: () => getFacilityFeatureFlags(),
  });
  const facilitiesQuery = useQuery({
    queryKey: ["pat-facilities"],
    queryFn: () => getFacilities(),
  });
  const boardQuery = useQuery({
    queryKey: ["pat-board", selectedFacility],
    queryFn: () => getPatBoard(selectedFacility || undefined),
  });

  const enabled = (featureFlagsQuery.data ?? []).some(
    (flag) => flag.feature_key === "pat_automation" && flag.enabled,
  );

  const issueMutation = useMutation({
    mutationFn: issuePatIntakeLink,
    onSuccess: async (payload) => {
      setErrorMessage(null);
      const url = typeof window !== "undefined" ? `${window.location.origin}${payload.path}` : payload.path;
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      }
      setStatusMessage(`PAT intake link issued and copied. Expires ${new Date(payload.expires_at).toLocaleString()}.`);
      await queryClient.invalidateQueries({ queryKey: ["pat-board"] });
    },
    onError: (error) => {
      setStatusMessage(null);
      setErrorMessage((error as Error).message);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: revokePatIntakeLink,
    onSuccess: async () => {
      setErrorMessage(null);
      setStatusMessage("PAT intake link revoked.");
      await queryClient.invalidateQueries({ queryKey: ["pat-board"] });
    },
    onError: (error) => {
      setStatusMessage(null);
      setErrorMessage((error as Error).message);
    },
  });

  const triageMutation = useMutation({
    mutationFn: generatePatAiTriage,
    onSuccess: async (payload) => {
      setErrorMessage(null);
      const risk = payload?.ai_assessment?.clinical_risk ?? "updated";
      setStatusMessage(`PAT AI triage generated. Current risk: ${risk}.`);
      await queryClient.invalidateQueries({ queryKey: ["pat-board"] });
    },
    onError: (error) => {
      setStatusMessage(null);
      setErrorMessage((error as Error).message);
    },
  });

  const reviewMutation = useMutation({
    mutationFn: ({ visitId, action, note }: { visitId: string; action: "approve" | "request_more_info" | "escalate"; note?: string }) =>
      updatePatReviewAction(visitId, action, note),
    onSuccess: async (payload, variables) => {
      setErrorMessage(null);
      setStatusMessage(`PAT review updated: ${payload?.review_status ?? variables.action}.`);
      await queryClient.invalidateQueries({ queryKey: ["pat-board"] });
    },
    onError: (error) => {
      setStatusMessage(null);
      setErrorMessage((error as Error).message);
    },
  });

  if (featureFlagsQuery.isLoading || facilitiesQuery.isLoading) {
    return <div className="p-6 text-sm text-slate-500">Loading PAT workspace...</div>;
  }

  if (!enabled) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
        PAT automation is not enabled for this tenant.
      </div>
    );
  }

  const facilities = facilitiesQuery.data ?? [];
  const visits = boardQuery.data ?? [];

  return (
    <div className="space-y-6">
      <section className="v11-section p-5">
        <p className="text-xs font-black uppercase tracking-widest text-[var(--brand-secondary)]">
          Facility operations
        </p>
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">PAT Intake</h1>
        <p className="mt-1 text-sm text-slate-600">
          Sprint 1 provides a staff-issued public intake link with draft autosave and submission tracking.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <select
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={selectedFacility}
            onChange={(event) => setSelectedFacility(event.target.value)}
          >
            <option value="">All facilities</option>
            {facilities.map((facility) => (
              <option key={facility.facility_id} value={facility.facility_id}>
                {facility.facility_name}
              </option>
            ))}
          </select>
        </div>
        {statusMessage ? <p className="mt-3 text-sm text-emerald-700">{statusMessage}</p> : null}
        {errorMessage ? <p className="mt-3 text-sm text-red-600">{errorMessage}</p> : null}
      </section>

      <section className="space-y-4">
        {boardQuery.isLoading ? <div className="text-sm text-slate-500">Loading PAT visits...</div> : null}
        {visits.map((visit) => {
          const activeLink = visit.active_link;
          const latestIntake = visit.latest_intake;

          return (
          <article key={visit.visit_id} className="v11-dashboard-card p-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-slate-900">{visit.facility_name}</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Visit {visit.visit_id.slice(0, 8)} · {visit.survey_date} · {visit.visit_type}
                </p>
              </div>
              {activeLink ? (
                <button
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700"
                  onClick={() => void revokeMutation.mutateAsync(activeLink.link_id)}
                >
                  Revoke Link
                </button>
              ) : (
                <button
                  className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white"
                  onClick={() => void issueMutation.mutateAsync(visit.visit_id)}
                >
                  Issue Intake Link
                </button>
              )}
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div className="v11-mini-metric text-left">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Link Status</p>
                {activeLink ? (
                  <div className="mt-2 text-sm text-slate-700">
                    <p>Expires: {new Date(activeLink.expires_at).toLocaleString()}</p>
                    <p>Public accesses: {activeLink.access_count}</p>
                    <p>Last accessed: {activeLink.last_accessed_at ? new Date(activeLink.last_accessed_at).toLocaleString() : "Not yet opened"}</p>
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-slate-600">No active intake link issued yet.</p>
                )}
              </div>
              <div className="v11-mini-metric text-left">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Intake Status</p>
                {latestIntake ? (
                  <div className="mt-2 text-sm text-slate-700">
                    <p>Status: {latestIntake.intake_status}</p>
                    <p>Completion: {latestIntake.completion_percent}%</p>
                    <p>Last saved: {latestIntake.autosaved_at ? new Date(latestIntake.autosaved_at).toLocaleString() : "Not yet saved"}</p>
                    {latestIntake.review_status ? (
                      <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                        <p className="font-semibold">Review queue: {latestIntake.review_status}</p>
                        <p className="mt-1">
                          Requested: {latestIntake.review_requested_at ? new Date(latestIntake.review_requested_at).toLocaleString() : "Pending"}
                        </p>
                        {latestIntake.review_updated_at ? (
                          <p className="mt-1">Last action: {new Date(latestIntake.review_updated_at).toLocaleString()}</p>
                        ) : null}
                        {latestIntake.submitted_at ? (
                          <p className="mt-1">
                            Turnaround: {Math.max(0, Math.round((new Date(latestIntake.review_completed_at ?? latestIntake.review_updated_at ?? new Date().toISOString()).getTime() - new Date(latestIntake.submitted_at).getTime()) / 3600000))}h
                          </p>
                        ) : null}
                        {latestIntake.review_note ? <p className="mt-1">{latestIntake.review_note}</p> : null}
                        {latestIntake.review_events.length > 0 ? (
                          <details className="mt-2">
                            <summary className="cursor-pointer font-semibold">Status history</summary>
                            <ul className="mt-2 space-y-1 text-[11px] text-amber-800">
                              {latestIntake.review_events.map((event) => (
                                <li key={event.review_event_id}>
                                  {new Date(event.created_at).toLocaleString()} · {event.review_status}
                                  {event.note ? ` · ${event.note}` : ""}
                                </li>
                              ))}
                            </ul>
                          </details>
                        ) : null}
                      </div>
                    ) : null}
                    {latestIntake.ai_assessment ? (
                      <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50 p-3 text-xs text-violet-900">
                        <div className="flex items-center gap-2 font-semibold">
                          <Sparkles size={12} />
                          AI triage: {latestIntake.ai_assessment.clinical_risk} risk
                        </div>
                        <p className="mt-1">
                          Confidence {Math.round(latestIntake.ai_assessment.risk_confidence * 100)}%.
                        </p>
                        {latestIntake.ai_assessment.flagged_items.length > 0 ? (
                          <ul className="mt-2 list-disc space-y-1 pl-4">
                            {latestIntake.ai_assessment.flagged_items.map((flag) => (
                              <li key={`${flag.code}-${flag.summary}`}>{flag.summary}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="mt-2">No elevated triage flags were produced.</p>
                        )}
                        <p className="mt-2">{latestIntake.ai_assessment.recommended_next_step}</p>
                        <p className="mt-2 text-[11px] text-violet-700">{latestIntake.ai_assessment.limitation_summary}</p>
                        {latestIntake.ai_generated_at ? (
                          <p className="mt-2 text-[11px] text-violet-700">
                            Generated {new Date(latestIntake.ai_generated_at).toLocaleString()}.
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-slate-600">No patient draft or submission captured yet.</p>
                )}
              </div>
            </div>
            {latestIntake?.intake_status === "submitted" ? (
              <div className="mt-4 space-y-3">
                <div className="flex justify-end">
                  <button
                    className="rounded-lg border border-violet-300 px-3 py-2 text-sm font-semibold text-violet-700"
                    onClick={() => void triageMutation.mutateAsync(visit.visit_id)}
                  >
                    Generate AI Triage
                  </button>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Nurse Review Actions</p>
                  <textarea
                    className="mt-2 min-h-20 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    placeholder="Optional review note"
                    value={reviewNotes[visit.visit_id] ?? ""}
                    onChange={(event) => setReviewNotes((current) => ({ ...current, [visit.visit_id]: event.target.value }))}
                  />
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white"
                      onClick={() => void reviewMutation.mutateAsync({ visitId: visit.visit_id, action: "approve", note: reviewNotes[visit.visit_id] })}
                    >
                      Approve
                    </button>
                    <button
                      className="rounded-lg bg-amber-500 px-3 py-2 text-sm font-semibold text-white"
                      onClick={() => void reviewMutation.mutateAsync({ visitId: visit.visit_id, action: "request_more_info", note: reviewNotes[visit.visit_id] })}
                    >
                      Request More Info
                    </button>
                    <button
                      className="rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white"
                      onClick={() => void reviewMutation.mutateAsync({ visitId: visit.visit_id, action: "escalate", note: reviewNotes[visit.visit_id] })}
                    >
                      Escalate
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </article>
        )})}
      </section>
    </div>
  );
}
